import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardPaste, StickyNote, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui';

/** Minimum characters for meaningful AI documentation (matches backend MIN_TRANSCRIPT_CHARS). */
const MIN_CONTENT_CHARS = 10;

interface TextInputPanelProps {
  note: string;
  onNoteChange: (text: string) => void;
  onAddNote: () => void;
}

export const TextInputPanel: React.FC<TextInputPanelProps> = ({
  note,
  onNoteChange,
  onAddNote,
}) => {
  const { t } = useTranslation();
  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        onNoteChange(text);
        toast.success(t('create.contentPasted'));
      } else {
        toast.error(t('create.clipboardEmpty'));
      }
    } catch {
      toast.error(t('create.clipboardError'));
    }
  }, [onNoteChange]);

  return (
    <div className="space-y-4">
      {/* Paste from Clipboard */}
      <button
        onClick={handlePaste}
        className="w-full p-4 rounded-xl border-2 border-dashed border-slate-300
                   hover:border-indigo-400 hover:bg-indigo-50/50
                   transition-all flex items-center justify-center gap-2 text-slate-600 hover:text-indigo-700"
      >
        <ClipboardPaste className="w-5 h-5" />
        <span className="font-medium">{t('create.pasteFromClipboard')}</span>
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-white text-slate-400">{t('create.orTypeBelow')}</span>
        </div>
      </div>

      {/* Textarea */}
      <textarea
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder={t('create.textPlaceholder')}
        rows={12}
        className="w-full px-4 py-3 border border-slate-300 rounded-xl
                   focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                   resize-none text-sm outline-none"
      />

      {/* Short content warning */}
      {note.trim().length > 0 && note.trim().length < MIN_CONTENT_CHARS && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{t('create.contentTooShort')}</p>
        </div>
      )}

      {/* Character counter */}
      <div className="flex flex-col items-center gap-2">
        <p className="text-xs text-slate-400">
          {note.length.toLocaleString()} {t('create.characters')}
        </p>
        <Button
          onClick={onAddNote}
          disabled={!note.trim()}
          variant="gradient"
          size="lg"
          shape="rounded"
        >
          <StickyNote className="w-5 h-5" />
          {t('create.captureNote')}
        </Button>
      </div>
    </div>
  );
};
